/**
 * ScrubBitmapCache
 * ----------------
 * Transient "use this bitmap for rendering" map keyed by elementId.
 * Populated by timeline/engine.ts:syncVideosAtPlayhead during the
 * paused/scrubbing branch when it finds a cached frame via
 * VideoRegistry.getCachedFrameNear. Read by
 * canvas/CanvasStageElementMedia.tsx:renderVideoElement to prefer the
 * cached bitmap over the (stale) live video element.
 *
 * Lifetime: cleared on play-start and on element unmount. Entries are
 * overwritten every frame during scrub as the cached frame closest to
 * the playhead changes. The bitmaps themselves are owned by
 * VideoRegistry's frameCache — do NOT call close() from this module.
 *
 * Tracked in docs/plans/2026-04-08-studio-canva-smoothness.md (Task D4).
 */

const _scrubBitmaps = new Map<string, ImageBitmap>();

/**
 * Stage a bitmap for the canvas renderer to use on the next draw.
 * The bitmap must remain owned by whoever stored it (typically
 * VideoRegistry's frameCache). This module never calls close().
 */
export function setScrubBitmap(elementId: string, bitmap: ImageBitmap): void {
  _scrubBitmaps.set(elementId, bitmap);
}

/**
 * Read the currently-staged bitmap for this element, or null if none.
 * Called from the hot path of `renderVideoElement` — must be cheap.
 */
export function getScrubBitmap(elementId: string): ImageBitmap | null {
  return _scrubBitmaps.get(elementId) ?? null;
}

/**
 * Drop the staged bitmap for one element. Called when the element is
 * removed from the editor or when playback resumes and the live video
 * is authoritative again.
 */
export function clearScrubBitmap(elementId: string): void {
  _scrubBitmaps.delete(elementId);
}

/**
 * Drop ALL staged bitmaps. Called from the canvas renderer on a
 * play-start effect so subsequent frames read from the live video
 * instead of the stale cached frame.
 */
export function clearAllScrubBitmaps(): void {
  _scrubBitmaps.clear();
}

/**
 * Read-only stats for the Perf HUD (D5). Returns the count of staged
 * bitmaps without exposing the bitmaps themselves.
 */
export function getScrubBitmapStats(): { staged: number } {
  return { staged: _scrubBitmaps.size };
}
